import * as React from 'react';
import {useEffect, useState} from 'react';
import * as webHelpers from '../../../helpers/WebHelpers';
import * as emailHelpers from '../../../helpers/EmailHelper';
import { ISearchResult, ISearchResultResponse } from "../../../interfaces/postcodesearch/ISearchResult";
import { Field, Form, Formik, FormikErrors, useFormik } from 'formik';
import { Button, Flex, FormControl, FormLabel, Grid, GridItem, Input, Menu, MenuButton, MenuItem, MenuList, propNames, Select, Stack, VStack } from '@chakra-ui/react';
import { CustomerType } from '../../../interfaces/jobsheets/ICustomer';

export interface ICustomerDetails {
    title: string,
    business_name: string | undefined,
    first_name: string,
    last_name: string,
    mobile: string | undefined,
    landline: string | undefined,
    email: string,
    address: {
        line1: string,
        line2: string,
        line3: string | undefined,
        line4: string | undefined,
        post_code: string
    }
}

interface ICustomerFormProps {
    updateCustomerDetails: (details: ICustomerDetails, err: string[]) => void;
    // quote: IVerbalQuote | null;
    details: ICustomerDetails | undefined;
    customerType: CustomerType;
    isReadOnly: boolean;
}

const Titles = ['Mr', 'Mrs', 'Miss', 'Ms', 'Dr', 'Other'];

export default function CustomerDetails({updateCustomerDetails, details, customerType, isReadOnly}: ICustomerFormProps) {
    let initialValues: ICustomerDetails;
    if (details != undefined) {
        initialValues = details;
    } else {
        initialValues = { title: '',
                        business_name: '',
                        first_name: '',
                        last_name: '',
                        mobile: '',
                        landline: '',
                        email: '',
                        address: {
                            post_code: '',
                            line1: '',
                            line2: '',
                            line3: '',
                            line4: ''
                        }};
    }

    const validateForm = () => {
        let errs: string[] = [];

        if (inputForm.values.title.length <= 0) {errs.push("Title is required")}
        if (inputForm.values.first_name.length <= 0) {errs.push("First Name is required")}
        if (inputForm.values.last_name.length <= 0) {errs.push("Last Name is required")}
        if ((!inputForm.values.mobile || inputForm.values.mobile.length <= 0)
            && (!inputForm.values.landline || inputForm.values.landline?.length <= 0)) {errs.push("Mobile or landline number required")}
        if (inputForm.values.email.length <= 0) {errs.push("Email is required")}
        else if (!emailHelpers.validateEmail(inputForm.values.email)) {errs.push("Email is invalid")}
        if (inputForm.values.address.post_code.length <= 0) {errs.push('Postcode is required')}
        if (inputForm.values.address.line1.length <= 0) {errs.push('Address Line 1 is required')}
        if (inputForm.values.address.line2.length <= 0) {errs.push('Address Line 2 is required')}

        return errs;
    }

    const inputForm = useFormik ({
        initialValues: initialValues,
        onSubmit: () => {}
    });

    const [searchResults, setSearchResults] = useState<ISearchResult[]>([]);
    const [isSearching, setIsSearching] = useState<boolean>(false);

    const handlePostcodeSearch = () => {
        setIsSearching(true);
        let searchResultPromise = webHelpers.getAsync<ISearchResultResponse>(
            `/get-addresses/${inputForm.values.address.post_code}`
        );
        searchResultPromise.then((data) => {
            if (data !== null) {
                setSearchResults(data.addresses);
            }
        }).catch((err) => {
            console.log("Error fetching addresses");
        });
        setIsSearching(false);
    };

    const createAddressString = (addr: ISearchResult): string => {
        let output = '';
        if (addr.Address1) output += addr.Address1;
        //if (addr.Address2) output += ', ' + addr.Address2;
        //if (addr.Address3) output += ', ' + addr.Address3;
        //if (addr.Address4) output += ', ' + addr.Address4;
        return output;
    }

    const handleAddressSelect = (item: ISearchResult): void => {
        inputForm.setFieldValue("address.line1", item.Address1);
        inputForm.setFieldValue("address.line2", item.Address2);
        inputForm.setFieldValue("address.line3", item.Address3);
        inputForm.setFieldValue("address.line4", item.Address4);
    };

    React.useEffect(() => {
        let details: ICustomerDetails = inputForm.values;
        details.business_name = details.business_name == "" ? undefined : details.business_name;
        details.mobile = details.mobile == "" ? undefined : details.mobile;
        details.landline = details.landline == "" ? undefined : details.landline;
        details.address.line3 = details.address.line3 == "" ? undefined : details.address.line3;
        details.address.line4 = details.address.line4 == "" ? undefined : details.address.line4;
        updateCustomerDetails(details, validateForm());
    }, [inputForm.values]);

    return (
        <Grid w='full' templateColumns={{ base: "repeat(1, 1fr)", lg: "repeat(2, 1fr)" }} gap={4}>
            <GridItem w='full'>
                <VStack mt={6} spacing={3.5}>
                    {(customerType == CustomerType.PrivateBusiness || customerType == CustomerType.PublicSector) &&
                        <Flex w='full'>
                            <FormControl variant="floating" isReadOnly={isReadOnly}>
                                <Input type='text' name="business_name" onChange={inputForm.handleChange} value={inputForm.values.business_name} placeholder=" "/>
                                <FormLabel>Business Name</FormLabel>
                            </FormControl>
                        </Flex>
                    }
                    
                    <Stack w='full' direction={{base: 'column', sm: 'row'}} spacing={3.5}>
                        {isReadOnly ? 
                            <FormControl variant="floating" isRequired isReadOnly={true} w={{base: '100%', sm: '30%'}}>
                                <Input type="text" name="first_name" value={inputForm.values.title} placeholder=" "/>
                                <FormLabel>Title</FormLabel>
                            </FormControl>
                            :
                            <FormControl isInvalid={inputForm.values.title.length <= 0} variant="floating" isRequired w={{base: '100%', sm: '30%'}}>
                                <Select name="title" onChange={inputForm.handleChange} value={inputForm.values.title} placeholder=" ">
                                    {Titles.map((title) =>
                                        <option value={title}>{title}</option>
                                    )}
                                </Select>
                                <FormLabel>Title</FormLabel>
                            </FormControl>
                        }
                        

                        <FormControl variant="floating" isRequired isInvalid={inputForm.values.first_name.length <= 0} isReadOnly={isReadOnly}>
                            <Input type="text" name="first_name" onChange={inputForm.handleChange} value={inputForm.values.first_name} placeholder=" "/>
                            <FormLabel>First Name</FormLabel>
                        </FormControl>
                    </Stack>

                    <Flex w='full'>
                        <FormControl variant="floating" isRequired isInvalid={inputForm.values.last_name.length <= 0} isReadOnly={isReadOnly}>
                            <Input type="text" name="last_name" onChange={inputForm.handleChange} value={inputForm.values.last_name} placeholder=" "/>
                            <FormLabel>Last Name</FormLabel>
                        </FormControl>
                    </Flex>

                    <Flex w='full'>
                        <FormControl variant="floating" isRequired isReadOnly={isReadOnly}>
                            <Input type="tel" name="mobile" onChange={inputForm.handleChange} value={inputForm.values.mobile} placeholder=" "/>
                            <FormLabel>Mobile Number</FormLabel>
                        </FormControl>
                    </Flex>

                    <Flex w='full'>
                        <FormControl variant="floating" isReadOnly={isReadOnly}>
                            <Input type="tel" name="landline" onChange={inputForm.handleChange} value={inputForm.values.landline} placeholder=" "/>
                            <FormLabel>Landline</FormLabel>
                        </FormControl>
                    </Flex>

                    <Flex w='full'>
                        <FormControl variant="floating" isInvalid={!(emailHelpers.validateEmail(inputForm.values.email))} isRequired isReadOnly={isReadOnly}>
                            <Input type="email" name="email" onChange={inputForm.handleChange} value={inputForm.values.email} placeholder=" "/>
                            <FormLabel>Email</FormLabel>
                        </FormControl>
                    </Flex>
                </VStack>
            </GridItem>

            <GridItem w='full'>
                <VStack spacing={3.5} mt={6}>
                    <Flex w='full'>
                        <FormControl variant="floating" isRequired isInvalid={inputForm.values.address.post_code.length <= 0} mr={5} isReadOnly={isReadOnly}>
                            <Input name="address.post_code" onChange={inputForm.handleChange} value={inputForm.values.address.post_code.toUpperCase()} placeholder=" "/>
                            <FormLabel>Post Code</FormLabel>
                        </FormControl>

                        {!isReadOnly && 
                            <Flex>
                                <Menu placement='bottom-end'>
                                    <MenuButton as={Button} className='md:ml-16' onClick={handlePostcodeSearch} colorScheme='blue'>Search</MenuButton>
                                    {searchResults.length > 0 ?
                                        <MenuList zIndex='dropdown' maxHeight={'50vh'} overflowY='scroll'>
                                            {searchResults?.map((item) => 
                                                <MenuItem overflowY='auto' onClick={() => handleAddressSelect(item)}>{createAddressString(item)}</MenuItem>
                                            )}
                                        </MenuList>
                                        :
                                        <MenuList>
                                            {isSearching ? "Finding addresses..." : "No Addresses Found"}
                                        </MenuList>
                                    }       
                                </Menu>
                            </Flex>
                        }
                    </Flex>

                    <Flex w='full'>
                        <FormControl variant="floating" isRequired isInvalid={inputForm.values.address.line1.length <= 0} isReadOnly={isReadOnly}>
                            <Input name="address.line1" onChange={inputForm.handleChange} value={inputForm.values.address.line1} placeholder=" "/>
                            <FormLabel>Line 1</FormLabel>
                        </FormControl>
                    </Flex>

                    <Flex w='full'>
                        <FormControl variant="floating" isRequired isInvalid={inputForm.values.address.line2.length <= 0} isReadOnly={isReadOnly}>
                            <Input name="address.line2" onChange={inputForm.handleChange} value={inputForm.values.address.line2} placeholder=" "/>
                            <FormLabel>Line 2</FormLabel>
                        </FormControl>
                    </Flex>

                    <Flex w='full'>
                        <FormControl variant="floating" isReadOnly={isReadOnly}>
                            <Input name="address.line3" onChange={inputForm.handleChange} value={inputForm.values.address.line3} placeholder=" "/>
                            <FormLabel>Line 3</FormLabel>
                        </FormControl>
                    </Flex>

                    <Flex w='full'>
                        <FormControl variant="floating" isReadOnly={isReadOnly}>
                            <Input name="address.line4" onChange={inputForm.handleChange} value={inputForm.values.address.line4} placeholder=" "/>
                            <FormLabel>Line 4</FormLabel>
                        </FormControl>
                    </Flex>
                </VStack>
            </GridItem>
        </Grid>
    );
}   